import React from 'react';
import { db } from '@/js/external-js/config/main_config';
import { isStandardEnabled, getEnabledModules } from '@/js/external-js/variableModules/utils/EnabledModulesManager';
import {
  faGear,
  faListTree,
  faBooks,
  faTelescope,
  faRankingStar,
  faBinary,
  faSubtitles,
  faFolderOpen,
  faBookOpen,
  faCabinetFiling
} from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { createIconComponent } from "@/utils/icon-utils";

export interface KeyDocumentItem {
  title: string;
  url: string;
  subtitle?: string;
  routeName?: string;
  icon: React.ComponentType<any> | React.ReactNode;
}

/**
 * Gets the key documents items for the sidebar based on enabled standards and document existence
 * @param tenancyId The active tenancy ID
 * @returns Promise resolving to an array of key document items
 */
export interface GetKeyDocumentItemsOptions {
  isEnterprise?: boolean;
}

export const getKeyDocumentItems = async (
  tenancyId: string,
  options: GetKeyDocumentItemsOptions = {}
): Promise<KeyDocumentItem[]> => {
  if (!tenancyId) return [];

  const { isEnterprise = false } = options;
  console.log('[getKeyDocumentItems] isEnterprise:', isEnterprise);
  const items: KeyDocumentItem[] = [];
  const promises: Promise<any>[] = [];

  // Check which standards are enabled
  const iso27001 = await isStandardEnabled('ISO 27001');
  const iso27701 = await isStandardEnabled('ISO 27701');
  const iso9001 = await isStandardEnabled('ISO 9001');
  const iso14001 = await isStandardEnabled('ISO 14001');
  const iso42001 = await isStandardEnabled('ISO 42001');
  const iso45001 = await isStandardEnabled('ISO 45001');
  const soc2 = await isStandardEnabled('SOC 2');

  // Check enabled modules for tenancy
  const enabledModules = await getEnabledModules();

  // Check if SMB 1001 is the only enabled module
  const isSMB1001Only = enabledModules.size === 1 && (enabledModules.values().next().value.toLowerCase() === 'smb 1001' ||enabledModules.values().next().value.toLowerCase() === 'smb1001');

  const keyDocsRef = db
    .collection('customers')
    .doc(tenancyId)
    .collection('product')
    .doc('policyPackages')
    .collection('ISO27001keyDocuments');

  // Add Configuration Standards if ISO 27701 is enabled
  // if (iso27701) {
  //   items.push({
  //     title: 'Configuration Standards',
  //     subtitle: 'Define how your systems should behave',
  //     url: '/configurationStandards',
  //     routeName: 'configuration-standards',
  //     icon: createIconComponent(faGear),
  //   });
  // }

  // SOA document
  promises.push(
    keyDocsRef.doc('ismsSoa').get().then((docSnap) => {
      if (!docSnap.exists) return;
      const data = docSnap.data();
      const newSOA = data?.newSOA || window.customerPlan === 'Starter' || window.customerPlan === 'starter';
      const title = 'Statement of Applicability';
      const subtitle = 'Otherwise known as your "SOA"';
      const url = newSOA ? '/soa' : '/isms-soa.html';
      items.push({
        title,
        subtitle,
        url,
        routeName: newSOA ? 'soa' : undefined,
        icon: createIconComponent(faListTree),
      });
    })
  );

  // ISMS Manual, Scope, and SIG if ISO 27001 is enabled and not enterprise
  if (iso27001 && !isEnterprise) {
    // promises.push(
    //   keyDocsRef.doc('ismsManual').get().then((docSnap) => {
    //     if (!docSnap.exists) return;
    //     const data = docSnap.data();
    //     const title = data?.cardDetails?.cardTitle || 'ISMS Manual';
    //     const subtitle = data?.cardDetails?.cardSubtitle || '';
    //     items.push({
    //       title,
    //       subtitle,
    //       url: '/isms-manual-db.html',
    //       icon: createIconComponent(faBooks),
    //     });
    //   })
    // );

    // promises.push(
    //   keyDocsRef.doc('ismsScope').get().then((docSnap) => {
    //     if (!docSnap.exists) return;
    //     const data = docSnap.data();
    //     const title = data?.cardDetails?.cardTitle || 'ISMS Scope';
    //     const subtitle = data?.cardDetails?.cardSubtitle || '';
    //     items.push({
    //       title,
    //       subtitle,
    //       url: '/isms-scope.html',
    //       icon: createIconComponent(faTelescope),
    //     });
    //   })
    // );

    // promises.push(
    //   keyDocsRef.doc('ismsSIG').get().then((docSnap) => {
    //     if (!docSnap.exists) return;
    //     const data = docSnap.data();
    //     const title = data?.cardDetails?.cardTitle || 'Special Interest Groups';
    //     const subtitle = data?.cardDetails?.cardSubtitle || '';
    //     items.push({
    //       title,
    //       subtitle,
    //       url: '/isms-sig.html',
    //       icon: createIconComponent(faRankingStar),
    //     });
    //   })
    // );
  }

  // SOC2 documents if SOC2 is enabled
  if (soc2) {
    items.push({
      title: 'SOC 2 Control Matrix',
      subtitle: 'Map trust services criteria to assurance activities',
      url: '/controlMatrix',
      routeName: 'soc2-control-matrix',
      icon: createIconComponent(faBinary),
    });
  }

  // Support Documentation
  promises.push(
    keyDocsRef.doc('otherDocs').get().then((docSnap) => {
      if (!docSnap.exists) return;
      const data = docSnap.data();
      const title = (isEnterprise) ? "Document Library" : data?.cardDetails?.cardTitle || 'Document Library';
      const subtitle = (isEnterprise) ? "Your repository of documents and resources." : data?.cardDetails?.cardSubtitle || 'Your repository of documents and resources.';
      items.push({
        title,
        subtitle,
        url: '/documentlibrary',
        routeName: 'documentlibrary',
        icon: createIconComponent(faFolderOpen),
      });
    })
  );

  // Always show Management System Guide for enterprise plans
  // if (isEnterprise) {
  //   items.push({
  //     title: 'System Guide',
  //     subtitle: 'Interactive guide for developing and maintaining your management system',
  //     url: '/msg',
  //     routeName: 'msg',
  //     icon: createIconComponent(faBookOpen),
  //   });
  // } else {
  //   // For non-enterprise, check feature flag
  //   const featuresRef = db
  //     .collection('customers')
  //     .doc(tenancyId)
  //     .collection('companySettings')
  //     .doc('features');

  //   promises.push(
  //     featuresRef.get().then((featuresDoc) => {
  //       if (featuresDoc.exists && featuresDoc.data()?.experimentalFeatures?.managementSystemGuide) {
  //         items.push({
  //           title: 'Management System Guide',
  //           subtitle: 'Interactive guide for developing and maintaining your management system',
  //           url: '/msg',
  //           routeName: 'msg',
  //           icon: createIconComponent(faBookOpen),
  //         });
  //       }
  //     })
  //   );
  // }

  if (iso27001 || iso9001 || iso14001 || iso42001 || iso45001) {
    items.push({
      title: 'System Guide',
      subtitle: 'Interactive guide for developing and maintaining your management system',
      url: '/msg',
      routeName: 'msg',
      icon: createIconComponent(faBookOpen),
    });
  }

  // Wait for all async operations to complete
  await Promise.all(promises);
  return items;
};